/*
 * MONEY FORMATTING — one source for the whole app.
 *
 * The currency is a WORKSPACE setting (tenants.currency, an ISO code like
 * 'USD'), chosen by an admin under Workspaces. Because money is formatted in
 * both React render code AND the non-React store, the ACTIVE symbol is held here
 * as a module value rather than threaded through every call site: the store sets
 * it from the workspace when the board loads and whenever an admin changes it
 * (see setActiveCurrency), and fmtMoney reads it synchronously. Amounts are held
 * ex-tax, as whole units.
 */

export interface Currency {
  /** ISO 4217 code — what is stored on the workspace. */
  code: string;
  /** What is shown in front of an amount. */
  symbol: string;
  /** The name a person picks from. */
  name: string;
}

/*
 * A CURATED LIST, not every currency in the world. These are the ones a
 * FlightDeck workspace is realistically run in; the code is stored, the symbol
 * is shown. The first entry is the fallback for a workspace that has never set
 * one, so it stays the dollar the app shipped with.
 */
export const CURRENCIES: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'Pound sterling' },
  { code: 'AUD', symbol: 'A$', name: 'Australian dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian dollar' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand dollar' },
  { code: 'TTD', symbol: 'TT$', name: 'Trinidad & Tobago dollar' },
  { code: 'JMD', symbol: 'J$', name: 'Jamaican dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian rupee' },
  { code: 'ZAR', symbol: 'R', name: 'South African rand' },
  { code: 'JPY', symbol: '¥', name: 'Japanese yen' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore dollar' },
  { code: 'AED', symbol: 'AED ', name: 'UAE dirham' },
];

/** The code a fresh workspace formats in until an admin chooses otherwise. */
export const DEFAULT_CURRENCY = 'USD';

/** The currency for a code, or the default when the code is blank or unknown. */
export function currencyByCode(code?: string | null): Currency {
  return CURRENCIES.find((c) => c.code === code) ?? CURRENCIES[0];
}

/*
 * THE ACTIVE SYMBOL. A module value so fmtMoney is a pure sync call everywhere,
 * including the store code that formats an audit line. It starts as the default
 * and is set once the workspace is known. The name is kept alongside it so a
 * money field can tell a screen reader which currency it is in (a bare "£" reads
 * as "pound sign", not "pounds"), without threading the workspace through.
 */
let activeSymbol = currencyByCode(DEFAULT_CURRENCY).symbol;
let activeName = currencyByCode(DEFAULT_CURRENCY).name;

/** Point every amount from now on at this workspace's currency. */
export function setActiveCurrency(code?: string | null): void {
  const cur = currencyByCode(code);
  activeSymbol = cur.symbol;
  activeName = cur.name;
}

/** The symbol amounts are currently drawn with. */
export function currencySymbol(): string {
  return activeSymbol;
}

/** The name of the active currency — for a screen-reader label, not display. */
export function currencyName(): string {
  return activeName;
}

/** A whole-unit, thousands-separated amount, or '' when there is no number. */
export function fmtMoney(n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '';
  // Minus sits OUTSIDE the symbol — "-TT$1,000", never "TT$-1,000" (R-money).
  const neg = n < 0 ? '-' : '';
  return `${neg}${activeSymbol}${Math.round(Math.abs(n)).toLocaleString()}`;
}

/**
 * Format an amount in a NAMED currency, not the active one. Used on a ledger line
 * that is in its own currency (a payment in TTD on a USD-base project): the line
 * shows its real amount with its own symbol, while the totals stay in the base
 * via fmtMoney. An unknown code falls back to the default symbol.
 */
export function fmtIn(code: string | null | undefined, n?: number | null): string {
  if (n == null || !Number.isFinite(n)) return '';
  const neg = n < 0 ? '-' : '';
  return `${neg}${currencyByCode(code).symbol}${Math.round(Math.abs(n)).toLocaleString()}`;
}

/** A zero in the active currency — the fallback where a total may be absent. */
export function zeroMoney(): string {
  return `${activeSymbol}0`;
}

/**
 * Parse a typed amount. A blank, non-numeric or non-positive value is
 * `undefined` — an amount is only meaningful when it is a real positive number,
 * so an empty field clears rather than storing a zero.
 */
export function asAmount(s: string): number | undefined {
  const n = Number(s.replace(/[^0-9.]/g, ''));
  return s.trim() && Number.isFinite(n) && n > 0 ? n : undefined;
}
